import React from 'react'
import styled, {DefaultTheme} from 'styled-components'
import type {MarginProps, PaddingProps, ResponsiveValue, StyleProps} from '../../'
import {convertLayoutProps} from '../../utils'

export interface TextProps extends MarginProps, PaddingProps, React.HTMLAttributes<HTMLElement> {
  as?: React.ElementType
  className?: string
  children: React.ReactNode
  $styles?: StyleProps
  $color?: keyof DefaultTheme['colors']
  $align?: ResponsiveValue<'left' | 'right' | 'center'>
  $transparency?: number | string
  $variant?: ResponsiveValue<keyof DefaultTheme['typography']['paragraphs']>
}

/**
 * This function should not be reused. It's used only to remove
 * the use of the `transparentize` function from `polish` because
 * we moved to using css variables.
 *
 * @deprecated
 */
const getOpacity = ($transparency: number | string) => {
  if (typeof $transparency !== 'number' || !$transparency) {
    return {}
  }

  return {
    opacity: 1 - $transparency,
  }
}

const TextWithRef = React.forwardRef<HTMLElement, TextProps>((props, ref) => {
  const {as: Element = 'p', children, ...restProps} = props

  return (
    <Element ref={ref} {...restProps}>
      {children}
    </Element>
  )
})

TextWithRef.displayName = 'TextWithRef'

const Text = styled(TextWithRef)<TextProps>`
  overflow-wrap: break-word;
  word-wrap: break-word;

  ${({theme, $variant}) => theme.expandVariants($variant, theme.typography.paragraphs)}

  ${({theme, $styles, $align, $color = 'primaryIndigo', $transparency = 0, ...props}) =>
    theme.normalizeStyles({
      ...convertLayoutProps(props),
      color: theme.colors[$color],
      textAlign: $align,
      ...getOpacity($transparency),
      ...$styles,
    })};
`

Text.displayName = 'Text'

export default Text
